import { combineReducers } from "redux";
import * as actions from "../actions";
import Constants from "../helpers/Constants";

// ===== 배송주문 관리 — 발주(주문 가져오기/발주확인/매칭/합포) =====
// list 계열 응답은 서버 직렬화 미확정(DNOrder) — 필드 접근은 라벨 헬퍼를 거쳐 방어적으로.

// 매칭 상태 — order.items 파생 (productId·parentId 둘 다 null 인 sku/product 라인 = 미매칭)
export const MATCHABLE_ITEM_TYPES = ["sku", "product"];

// 세트 구성품(파생) 라인 판정 — 1차: 직렬화된 referType/referId(referId=원 라인 id),
// 폴백: referType/referId 미직렬화 응답 대비 matchType=set + parentId(원 라인은 parentId null)
export const isSetComponentItem = (it) =>
  !!(it.referType && it.referId) || (it.matchType === "set" && !!it.parentId);

export const getUnmatchedItems = (order) => {
  const items = order?.items || order?.orderItems || [];
  return items.filter(
    (it) =>
      MATCHABLE_ITEM_TYPES.includes(it.type) &&
      !isSetComponentItem(it) &&
      it.matchType !== "set" &&
      !(it.productId && it.parentId),
  );
};

export const isFullyMatched = (order) => getUnmatchedItems(order).length === 0;

export const getMatchStatusLabel = (order) =>
  isFullyMatched(order) ? "매칭완료" : "미매칭";

// 결제 상태 배지 — 계약 최종본: payStatus("paid"|"pending") + 부분취소는 amountReturned>0
// (라인 단위 취소는 item.returnedQuantity>0). payStatus 없는 응답 대비 state 폴백.
export const getPayStatusLabel = (order) => {
  if (!order) return "-";
  if ((order.amountReturned || 0) > 0) return "부분취소";
  if (order.payStatus) {
    return order.payStatus === "paid" ? "결제완료" : "입금대기";
  }
  if (order.paidAt || order.state === "PAID" || order.state === "paid") {
    return "결제완료";
  }
  return "입금대기";
};

// 수동발주 주문은 서버가 origin="pos"로 기록(createManualOrder — 라벨맵 키 "manual"과
// 상충, 정본 확인 중) → pos 도 "수동 발주"로 표시(우진 2026-07-20: pos 원문 노출 금지)
export const getOriginLabel = (origin) =>
  Constants.DELIVERY_ORIGIN_LABELS[origin] ||
  (origin === "pos" ? "수동 발주" : origin) ||
  "-";

// 관리번호 표시 = numberPrefix-number (발주확인 시 발번)
export const getOrderNumberLabel = (order) =>
  order?.numberPrefix && order?.number
    ? `${order.numberPrefix}-${order.number}`
    : null;

const initialStatus = {
  isFetching: false,
  error: null,
};

// 서브도메인별 status 리듀서 생성기 — fetching 트리거 액션 / SUCCESS / FAILURE 매핑
function makeStatus(fetchingTypes, successType, failureType) {
  return function status(state = initialStatus, action) {
    if (fetchingTypes.includes(action.type)) {
      return { isFetching: true, error: null };
    }
    switch (action.type) {
      case successType:
        return initialStatus;
      case failureType:
        return { isFetching: false, error: action.error };
      default:
        return state;
    }
  };
}

const status = makeStatus(
  [
    actions.GET_PRE_CONFIRM_ORDERS,
    actions.GET_DELIVERY_STAGE_ORDERS,
    actions.GET_CONFIRM_BATCHES,
    actions.GET_ITEM_LINKS,
    actions.RELOAD_DELIVERY_ORDERS,
    actions.DELETE_UNCONFIRMED_ORDERS,
    actions.DELETE_ITEM_LINK,
  ],
  actions.SET_DELIVERY_STATUS_SUCCESS,
  actions.SET_DELIVERY_STATUS_FAILURE,
);

const importStatus = makeStatus(
  [actions.IMPORT_CHANNEL_ORDERS],
  actions.SET_DELIVERY_IMPORT_STATUS_SUCCESS,
  actions.SET_DELIVERY_IMPORT_STATUS_FAILURE,
);

const confirmStatus = makeStatus(
  [actions.CONFIRM_DELIVERY_ORDERS, actions.CANCEL_CONFIRM_ORDERS],
  actions.SET_DELIVERY_CONFIRM_STATUS_SUCCESS,
  actions.SET_DELIVERY_CONFIRM_STATUS_FAILURE,
);

const matchStatus = makeStatus(
  [actions.AUTO_MATCH_DELIVERY_ORDERS, actions.SAVE_MANUAL_MATCH],
  actions.SET_DELIVERY_MATCH_STATUS_SUCCESS,
  actions.SET_DELIVERY_MATCH_STATUS_FAILURE,
);

const mergeStatus = makeStatus(
  [
    actions.MERGE_CHECK_DELIVERY_ORDERS,
    actions.CONFIRM_MERGE_ORDERS,
    actions.EXCLUDE_MERGE_ORDERS,
    actions.MANUAL_MERGE_ORDERS,
    actions.SET_DELIVERY_MERGE_BLOCKED,
    // 합포 화면 멤버 행 합포금지 — 누락으로 오버레이·중복클릭 가드가 안 걸리던 것 보강(우진 7/24)
    actions.BLOCK_MERGE_GROUP,
    actions.CS_BULK_MERGE_BLOCK,
  ],
  actions.SET_DELIVERY_MERGE_STATUS_SUCCESS,
  actions.SET_DELIVERY_MERGE_STATUS_FAILURE,
);

const manualOrderStatus = makeStatus(
  // 주문복사(COPY_DELIVERY_ORDER)도 manualOrder 결과 슬라이스 공유 — isFetching 포함
  [
    actions.CREATE_MANUAL_ORDER,
    actions.UPLOAD_MANUAL_ORDER_ROWS,
    actions.COPY_DELIVERY_ORDER,
  ],
  actions.SET_MANUAL_ORDER_STATUS_SUCCESS,
  actions.SET_MANUAL_ORDER_STATUS_FAILURE,
);

// 발주전(발주대기) 주문 — listPreConfirmOrders 응답 전량 보관(서버 페이지네이션 미지원)
// ※ 합포 무시/금지 낙관 숨김(mergeHiddenOrderIds)은 최상위 delivery 리듀서가 교차 필터.
function preConfirmOrders(state = [], action) {
  switch (action.type) {
    case actions.RECEIVE_PRE_CONFIRM_ORDERS:
      return action.orders || [];
    default:
      return state;
  }
}

// 합포 무시/금지 낙관 숨김 id(우진 7/24) — 무시·금지 성공 시 사가가 등록. 두 처리 모두
// 단포 자동 발주완료 = 발주대기 이탈이 서버 최종 상태지만, updateOrder(DDB)→스트림→RDB
// 동기화 랙 동안 재조회(listPreConfirmOrders)가 stale 주문을 되돌려줘 그룹이 화면에
// 되살아남 → id 를 유지하며 계속 걸러낸다. 신선한 응답에서 주문이 빠지면(동기화 완료)
// id 도 자동 정리 — 발주취소 복귀 등 정당한 재등장 주문을 세션 내내 숨기지 않기 위함.
function mergeHiddenOrderIds(state = [], action) {
  switch (action.type) {
    case actions.HIDE_PRE_CONFIRM_ORDERS: {
      const orderIds = (action.orderIds || []).filter(Boolean);
      if (orderIds.length === 0) return state;
      return Array.from(new Set([...state, ...orderIds]));
    }
    case actions.RECEIVE_PRE_CONFIRM_ORDERS: {
      if (state.length === 0) return state;
      const present = new Set((action.orders || []).map((order) => order.id));
      const kept = state.filter((id) => present.has(id));
      return kept.length === state.length ? state : kept;
    }
    default:
      return state;
  }
}

// 발주 단계 nav — {order:{count,orders}, matching:{count,orders}, merge:{count,groups,orders}}
const INITIAL_STAGE_ORDERS = {
  order: { count: 0, orders: [] },
  matching: { count: 0, orders: [] },
  merge: { count: 0, groups: {}, orders: [] },
};

// merge 버킷을 합포 화면 시맨틱으로 정규화(우진 7/24 — nav 뱃지 4 vs 화면 2 잔존):
// 서버는 mergeGroupId 보유 주문 전부를 세지만 화면은 "금지 제외 + 2건 이상 그룹"만 렌더 →
// 뱃지도 동일 기준으로. mergeBlocked 는 서버 19번 ③으로 _stageRow 직렬화됨(구버전 응답은
// 필드 부재 → 필터 자연 통과). 숨김 id(mergeHiddenOrderIds) 교차 필터는 최상위 delivery
// 리듀서가 수행.
function normalizeStageMerge(stage) {
  if (!stage) return stage;
  let next = stage;
  const merge = stage.merge;
  if (merge && merge.groups) {
    const groups = {};
    Object.entries(merge.groups).forEach(([gid, rows]) => {
      // 화면 파생(DeliveryMerge :124)과 동일: 금지 주문 제외 후 2건 이상 그룹만
      const visible = (rows || []).filter((row) => !row.mergeBlocked);
      if (visible.length >= 2) groups[gid] = visible;
    });
    const orders = [].concat(...Object.values(groups));
    if (orders.length !== (merge.orders || []).length) {
      next = { ...next, merge: { ...merge, groups, orders, count: orders.length } };
    }
  }
  // 발주 뱃지 = 발주 리스트와 동일 기준(!mergeGroupId — 합포 후보는 합포 탭 분리).
  // 서버 order.count 는 합포의심 포함 총수라 리스트 "전체 N"과 어긋남(서버 20번 실측).
  const order = stage.order;
  if (order && Array.isArray(order.orders)) {
    const listCount = order.orders.filter((row) => !row.mergeGroupId).length;
    if (listCount !== order.count) {
      next = next === stage ? { ...stage } : next;
      next.order = { ...order, count: listCount };
    }
  }
  return next;
}

function stageOrders(state = INITIAL_STAGE_ORDERS, action) {
  switch (action.type) {
    case actions.RECEIVE_DELIVERY_STAGE_ORDERS:
      return normalizeStageMerge(action.stageOrders) || INITIAL_STAGE_ORDERS;
    default:
      return state;
  }
}

// 발주차수 — listConfirmBatches 응답(최신순 배열)
function confirmBatches(state = [], action) {
  switch (action.type) {
    case actions.RECEIVE_CONFIRM_BATCHES:
      return action.batches || [];
    default:
      return state;
  }
}

// 매칭내역 — listItemLinks 응답 {count, groups}
function itemLinks(state = { count: 0, groups: [] }, action) {
  switch (action.type) {
    case actions.RECEIVE_ITEM_LINKS:
      return action.itemLinks || { count: 0, groups: [] };
    default:
      return state;
  }
}

// 가져오기 결과 — {origin: 건수}. 수집 결과 모달 표시용(닫으면 clear)
function importResult(state = null, action) {
  switch (action.type) {
    case actions.RECEIVE_IMPORT_CHANNEL_ORDERS_RESULT:
      return action.result;
    case actions.CLEAR_IMPORT_CHANNEL_ORDERS_RESULT:
    case actions.IMPORT_CHANNEL_ORDERS:
      return null;
    default:
      return state;
  }
}

// 마지막 동기화 시각 — 가져오기/재동기화 성공 시 갱신(action.syncedAt).
// 발주 화면 이탈 후에도 유지되도록 redux 보관(QA 7/20). 서버 필드 부재라 세션
// 한정 // todo 서버가 동기화 시각 제공 시 대체(suspect)
function lastSyncedAt(state = null, action) {
  switch (action.type) {
    case actions.RECEIVE_IMPORT_CHANNEL_ORDERS_RESULT:
    case actions.RECEIVE_RELOAD_RESULT:
      return action.syncedAt || state;
    default:
      return state;
  }
}

// 재동기화 결과 — 계약 최종본: 응답에 skippedOrigins(재동기화 미지원 채널) 포함.
// 그 외 필드는 서버 직렬화 미확정 — 접근은 방어적으로.
function reloadResult(state = null, action) {
  switch (action.type) {
    case actions.RECEIVE_RELOAD_RESULT:
      return action.result;
    case actions.CLEAR_RELOAD_RESULT:
    case actions.RELOAD_DELIVERY_ORDERS:
      return null;
    default:
      return state;
  }
}

// 발주확인 결과 — {confirmed, skipped, unmatched, mergeCandidate, eventId, batchId, seq, numbers, channelPush}
function confirmResult(state = null, action) {
  switch (action.type) {
    case actions.RECEIVE_CONFIRM_DELIVERY_ORDERS_RESULT:
      return action.result;
    case actions.CLEAR_CONFIRM_DELIVERY_ORDERS_RESULT:
    case actions.CONFIRM_DELIVERY_ORDERS:
      return null;
    default:
      return state;
  }
}

// 자동매칭 결과 — {orders, matched, unmatched, eventId}
function autoMatchResult(state = null, action) {
  switch (action.type) {
    case actions.RECEIVE_AUTO_MATCH_RESULT:
      return action.result;
    case actions.CLEAR_AUTO_MATCH_RESULT:
    case actions.AUTO_MATCH_DELIVERY_ORDERS:
      return null;
    default:
      return state;
  }
}

// 합포검사 결과 — {checked, candidates}
function mergeCheckResult(state = null, action) {
  switch (action.type) {
    case actions.RECEIVE_MERGE_CHECK_RESULT:
      return action.result;
    case actions.MERGE_CHECK_DELIVERY_ORDERS:
      return null;
    default:
      return state;
  }
}

// 합포 확정/제외/수동합포 결과
function mergeResult(state = null, action) {
  switch (action.type) {
    case actions.RECEIVE_MERGE_RESULT:
      return action.result;
    case actions.CONFIRM_MERGE_ORDERS:
    case actions.EXCLUDE_MERGE_ORDERS:
    case actions.MANUAL_MERGE_ORDERS:
      return null;
    default:
      return state;
  }
}

// 발주취소 결과 — {canceled, skipped, batchId, channelPush, blockedByTracking?}
// blockedByTracking: 송장 발번되어 취소에서 제외된 주문 목록(서버 직렬화 미확정 — 방어적 접근)
function cancelResult(state = null, action) {
  switch (action.type) {
    case actions.RECEIVE_CANCEL_CONFIRM_ORDERS_RESULT:
      return action.result;
    case actions.CLEAR_CANCEL_CONFIRM_ORDERS_RESULT:
    case actions.CANCEL_CONFIRM_ORDERS:
      return null;
    default:
      return state;
  }
}

// 수동발주 단건 결과 — {status: "created"|"duplicate", id?, originId}
function manualOrderResult(state = null, action) {
  switch (action.type) {
    case actions.RECEIVE_CREATE_MANUAL_ORDER_RESULT:
      return action.result;
    case actions.CLEAR_MANUAL_ORDER_RESULT:
    case actions.CREATE_MANUAL_ORDER:
      return null;
    default:
      return state;
  }
}

// 수동발주 일괄(엑셀) 결과 — {created, duplicate:[], error:[]}
function uploadRowsResult(state = null, action) {
  switch (action.type) {
    case actions.RECEIVE_UPLOAD_MANUAL_ORDER_ROWS_RESULT:
      return action.result;
    case actions.CLEAR_MANUAL_ORDER_RESULT:
    case actions.UPLOAD_MANUAL_ORDER_ROWS:
      return null;
    default:
      return state;
  }
}

const combined = combineReducers({
  status,
  importStatus,
  confirmStatus,
  matchStatus,
  mergeStatus,
  manualOrderStatus,
  preConfirmOrders,
  mergeHiddenOrderIds,
  stageOrders,
  confirmBatches,
  itemLinks,
  importResult,
  lastSyncedAt,
  reloadResult,
  confirmResult,
  autoMatchResult,
  mergeCheckResult,
  mergeResult,
  cancelResult,
  manualOrderResult,
  uploadRowsResult,
});

// 최상위 후처리 — combineReducers 슬라이스는 서로를 못 보므로 여기서 교차 필터:
// preConfirmOrders 와 stageOrders.merge(nav 뱃지)에서 낙관 숨김 id(mergeHiddenOrderIds)를
// 제거해 저장한다. 숨김 등록 직후(HIDE)와 stale 재조회 수신(RECEIVE 2종) 시점만 해당.
// 슬라이스 shape 는 그대로라 외부 셀렉터(reducers/index.js state.delivery.*)는 영향 없음.
function filterStageMergeHidden(stage, hidden) {
  const merge = stage && stage.merge;
  if (!merge || !merge.groups || (merge.orders || []).length === 0) return stage;
  let changed = false;
  const groups = {};
  Object.entries(merge.groups).forEach(([gid, rows]) => {
    const kept = (rows || []).filter((row) => !hidden.has(row.id));
    if (kept.length !== (rows || []).length) changed = true;
    // 숨김 후 1건 잔재 그룹은 화면과 동일하게 뱃지에서도 제외
    if (kept.length >= 2) groups[gid] = kept;
    else if (kept.length > 0) changed = true;
  });
  if (!changed) return stage;
  const orders = [].concat(...Object.values(groups));
  return { ...stage, merge: { ...merge, groups, orders, count: orders.length } };
}

export default function delivery(state, action) {
  const next = combined(state, action);
  if (next.mergeHiddenOrderIds.length === 0) return next;
  const crossActions = [
    actions.HIDE_PRE_CONFIRM_ORDERS,
    actions.RECEIVE_PRE_CONFIRM_ORDERS,
    actions.RECEIVE_DELIVERY_STAGE_ORDERS,
  ];
  if (!crossActions.includes(action.type)) return next;
  const hidden = new Set(next.mergeHiddenOrderIds);
  let result = next;
  const visible = next.preConfirmOrders.filter(
    (order) => !hidden.has(order.id),
  );
  if (visible.length !== next.preConfirmOrders.length) {
    result = { ...result, preConfirmOrders: visible };
  }
  const stage = filterStageMergeHidden(next.stageOrders, hidden);
  if (stage !== next.stageOrders) {
    result = result === next ? { ...next, stageOrders: stage } : { ...result, stageOrders: stage };
  }
  return result;
}
